import React, { useCallback, useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getQuestions,
  getSubjects,
  getPurposes,
  getObjectives,
  getTopics,
  getSubtopics,
  getStatuses,
  getStatusCount,
  getQuestionFolders,
} from '../../services/questionService';
import { setFilterState, getFilterState } from '../../utils/filterState';
import {
  PencilIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import { ArrowUpTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import { UserContext } from '../../utils/contexts/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import TableWithServerOrdering from '../../components/Table/TableWithServerOrdering';
import Select from '../../components/forms/Select/Select';
import { filterSelectStyles } from '../../components/forms/Select/styles';
import FolderBar from '../../components/QuestionFolders/FolderBar';
import CreateQuestionMenu from '../../components/QuestionFolders/CreateQuestionMenu';
import UploadQuestionsDialog from '../../components/QuestionFolders/UploadQuestionsDialog';
import FolderNameDialog, { examSavedMessage } from '../../components/QuestionFolders/FolderNameDialog';
import AddSubjectDialog from '../../components/QuestionFolders/AddSubjectDialog';
import SubjectSettingsDialog from '../../components/QuestionFolders/SubjectSettingsDialog';
import DeleteFolderDialog from '../../components/QuestionFolders/DeleteFolderDialog';
import ShareFolderDialog from '../../components/QuestionFolders/ShareFolderDialog';
import FolderMatrix from '../../components/QuestionFolders/FolderMatrix';
import FolderTasks from '../../components/QuestionFolders/tasks/FolderTasks';
import OverflowMenu from '../../components/ui/OverflowMenu';
import { HelpTitle } from '../../components/Help/HelpButton';
import MyQuestionsGuide from '../../components/Help/guides/MyQuestionsGuide';
import {
  canCreateIn,
  canSeeTasksIn,
  folderPath,
  folderTree,
  isAdminUser,
  isSubjectFolder,
  subjectFolderOptions,
} from '../../utils/folders';
import BankTabs, { usePassagesAvailable } from '../../components/Passages/BankTabs';
import { DifficultyBadge, DIFFICULTY_LEVELS, difficultyLabel } from '../../components/Difficulty/Difficulty';
import StatusFilterBar from '../../components/QuestionList/StatusFilterBar';
import FilterPanel from '../../components/QuestionList/FilterPanel';
import ResultsBar, { Pager } from '../../components/QuestionList/ResultsBar';
import QuestionCards from '../../components/QuestionList/QuestionCards';
import { QuestionStatusBadge } from '../../components/QuestionList/questionStatus';
import { Dash, QuestionSummary, ReviewCounts, SubjectCell } from '../../components/QuestionList/cells';
import { Spinner, useMediaQuery } from '../Exams/TestBuilder/ui';
import SelectionBar from '../../components/QuestionFolders/SelectionBar';
import { isDraftQuestion } from '../Questions/Table';
import useQuestionsPreview, { MAX_PREVIEW } from './useQuestionsPreview';
import { formatDate } from '../../utils/formatDate';
import { toast } from 'react-toastify';
import { deleteQuestion } from '../../services/MyQuestions/QuestionService';
import { isCanceledError } from '../../utils/examError';
import { getApiErrorMessage } from '../../utils/apiError';

const PAGE_SIZE = 50;

const boxClass = 'h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-40';

// The ticks, read by the table's boxes from here: its columns stay the same objects, so ticking one question does
// not draw the whole table anew (nor take the focus off the box). {questions, selectedIds, toggleSelect,
// togglePage, selectBox}.
const PreviewSelection = React.createContext(null);

// A row's box; a draft's is off, with why in its name and tooltip.
function RowCheckbox({ question }) {
  const { selectedIds, toggleSelect, selectBox } = useContext(PreviewSelection);
  const box = selectBox(question);
  return (
    <input
      type="checkbox"
      checked={!box.disabled && selectedIds.has(question.uuid)}
      disabled={!!box.disabled}
      onChange={() => toggleSelect(question)}
      aria-label={box.label}
      title={box.disabled ? box.label : undefined}
      className={boxClass}
    />
  );
}

// The table's header box: every question of the page that can be ticked (not the drafts), half-ticked when some are.
function PageCheckbox({ label }) {
  const { questions, selectedIds, togglePage: onToggle } = useContext(PreviewSelection);
  const ref = useRef(null);
  const choosable = questions.filter((q) => !isDraftQuestion(q));
  const picked = choosable.filter((q) => selectedIds.has(q.uuid)).length;
  const all = choosable.length > 0 && picked === choosable.length;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = picked > 0 && !all;
  });
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={all}
      disabled={!choosable.length}
      onChange={() => onToggle(choosable, !all)}
      aria-label={label}
      title={label}
      className={boxClass}
    />
  );
}

// A secondary header action next to the one main button (wide screens; phones have it in "⋯"), as in FolderBar.
const quietButton =
  'inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';

// A draft has no question page yet, so it opens in its editor.
const questionHref = (question) =>
  question?.status?.code === 'DRAFT' ? `/my-questions/draft/${question.uuid}` : `/questions/${question.uuid}`;
// The pencil follows the row's can_edit (the role in its subject and its status); a list without it (an older
// backend) keeps the old rule: anything but approved questions. A draft is edited in the draft editor.
const editHref = (question) => {
  const editable = typeof question?.can_edit === 'boolean' ? question.can_edit : question?.status?.code !== 'APPROVED';
  if (!editable) return null;
  return `/my-questions/${question?.status?.code === 'DRAFT' ? 'draft' : 'edit'}/${question.uuid}`;
};

const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || '';
const filterValue = (value) => (value !== 'all' ? value : undefined);

export default function MyQuestions() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { user } = useContext(UserContext);
  const initialFilters = useMemo(() => getFilterState(), []);
  const navigate = useNavigate();
  // Phones get the list as cards instead of the wide table.
  const wide = useMediaQuery('(min-width: 768px)');
  // A new list/counts request aborts the previous one, so a late response can't replace the current results.
  const abortControllerRef = useRef(null);
  const countsControllerRef = useRef(null);
  // Page of the latest list request: the page effect skips the page 1 that a filter reset already fetched.
  const fetchedPageRef = useRef(null);

  const [questions, setQuestions] = useState([]);
  // Questions ticked to be seen as a test (kept from page to page, cleared with another folder).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { open: openPreview, loading: previewLoading, preview } = useQuestionsPreview();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [count, setCount] = useState(0);
  // Import dialog: uploadFolder null = the folder is picked in the dialog, a folder = import into it.
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFolder, setUploadFolder] = useState(null);
  // Folders are navigation only here: counts cover my questions (with my drafts); hidden on an old backend.
  const myUuid = user?.uuid;
  const [folder, setFolder] = useState(initialFilters.folder || 'all');
  const [folders, setFolders] = useState([]);
  const [foldersAvailable, setFoldersAvailable] = useState(false);
  const [noFolderCount, setNoFolderCount] = useState(null);
  // The Texts tab: hidden when the backend has no passages API (old deployment).
  const passagesAvailable = usePassagesAvailable();
  // Exams and subjects are made by admins in the question bank. Without the global create_question the
  // questions are created and imported inside an exam's subject (the role there allows it).
  const perms = user?.perms || [];
  const canCreateWithoutFolder = perms.includes('create_question');
  // Admins manage exams and their subjects here too, as in the question bank.
  const isAdmin = isAdminUser(user);
  // Where the reader creates questions (CreateQuestionMenu's places: the subjects their role writes in, and "no
  // folder" with the global right): the guide asks to pick a subject only when there are several.
  const createPlaces = useMemo(
    () =>
      subjectFolderOptions(foldersAvailable ? folders : [], (f) => canCreateIn(f.role)).reduce(
        (sum, group) => sum + group.options.length,
        0,
      ) + (canCreateWithoutFolder ? 1 : 0),
    [folders, foldersAvailable, canCreateWithoutFolder],
  );
  const [folderDialog, setFolderDialog] = useState({ open: false, folder: null });
  const [subjectDialog, setSubjectDialog] = useState({ open: false, exam: null });
  const [subjectSettings, setSubjectSettings] = useState({ open: false, folder: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, folder: null });
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState(initialFilters.grade || 'all');
  const [subject, setSubject] = useState(initialFilters.subject || 'all');
  const [purpose, setPurpose] = useState(initialFilters.purpose || 'all');
  const [objective, setObjective] = useState(initialFilters.objective || 'all');
  const [topic, setTopic] = useState(initialFilters.topic || 'all');
  const [subtopic, setSubtopic] = useState(initialFilters.subtopic || 'all');
  const [status, setStatus] = useState(initialFilters.status || 'all');
  // "all", "null" (no difficulty) or A / B / C
  const [difficulty, setDifficulty] = useState(initialFilters.difficulty || 'all');
  const [ordering, setOrdering] = useState(initialFilters.ordering || 'created');

  const [subjectOptions, setSubjectOptions] = useState([]);
  const [purposeOptions, setPurposeOptions] = useState([]);
  const [objectiveOptions, setObjectiveOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [subtopicOptions, setSubtopicOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);

  // Every list filter (my questions only); the status counts reuse them without the status.
  const filterParams = {
    author: myUuid,
    folder: filterValue(folder),
    subject: filterValue(subject),
    grade: filterValue(grade),
    purpose: filterValue(purpose),
    objective: filterValue(objective),
    topic: filterValue(topic),
    subtopic: filterValue(subtopic),
    status: filterValue(status),
    difficulty: filterValue(difficulty),
    search: search || undefined,
  };

  // Inside a subject the upload/create actions are in the folder card (FolderBar) instead of the header.
  const { exams, byUuid } = useMemo(() => folderTree(folders), [folders]);
  const currentSubject = isSubjectFolder(byUuid.get(folder)) ? byUuid.get(folder) : null;
  const inSubject = !!currentSubject;
  const showFolder = folder === 'all';
  // Topics belong to a catalog subject: the open subject folder's, else the subject filter's.
  const topicSubject = currentSubject?.subject?.uuid || (subject !== 'all' && subject !== 'null' ? subject : undefined);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // A newer subject/grade (or topic) aborts the older options request, so a late response can't replace the options.
  useEffect(() => {
    if (!initialDataLoaded) return undefined;
    const controller = new AbortController();
    fetchTopics(controller.signal);
    return () => controller.abort();
  }, [topicSubject, grade, initialDataLoaded]);

  // Another subject's folder: the topic picked in the previous subject does not apply there.
  const topicSubjectRef = useRef(topicSubject);
  useEffect(() => {
    const previous = topicSubjectRef.current;
    topicSubjectRef.current = topicSubject;
    if (previous && topicSubject && previous !== topicSubject) {
      setTopic('all');
      setSubtopic('all');
    }
  }, [topicSubject]);

  useEffect(() => {
    if (!initialDataLoaded) return undefined;
    const controller = new AbortController();
    fetchSubtopics(controller.signal);
    return () => controller.abort();
  }, [topic, initialDataLoaded]);

  useEffect(() => {
    // Wait for the user: without author the list would show everyone's questions.
    if (initialDataLoaded && myUuid) {
      setPage(1);
      fetchQuestions(1);
      fetchStatusCount();
    }
  }, [initialDataLoaded, grade, subject, purpose, objective, topic, subtopic, status, difficulty, search, ordering, folder, myUuid]);

  useEffect(() => {
    if (initialDataLoaded && page !== fetchedPageRef.current) {
      fetchQuestions(page);
    }
  }, [page]);

  const loadFolders = React.useCallback(
    // Only a failed first load hides the folder bar (old backend); a failed refresh keeps it.
    (signal, { initial = false } = {}) => {
      if (!myUuid) return;
      Promise.all([
        getQuestionFolders({ params: { author: myUuid }, signal }),
        getQuestions({ params: { page: 1, page_size: 1, folder: 'null', author: myUuid }, signal }).catch(() => null),
      ])
        .then(([list, noFolder]) => {
          if (!Array.isArray(list)) throw new Error('Unexpected question_folders response');
          setFolders(list);
          setNoFolderCount(typeof noFolder?.count === 'number' ? noFolder.count : null);
          setFoldersAvailable(true);
          setFolder((prev) => (prev === 'all' || prev === 'null' || list.some((f) => f.uuid === prev) ? prev : 'all'));
        })
        .catch((err) => {
          if (isCanceledError(err) || !initial) return;
          setFoldersAvailable(false);
          setFolders([]);
          setFolder('all');
        });
    },
    [myUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadFolders(controller.signal, { initial: true });
    return () => controller.abort();
  }, [loadFolders]);

  const fetchFilterOptions = async () => {
    const controller = new AbortController();
    const opts = { signal: controller.signal };
    const extract = (res) => {
      if (res.status !== 'fulfilled') return [];
      const data = res.value;
      return Array.isArray(data) ? data : (data?.results || []);
    };
    const [subjects, purposes, objectives, statuses] = await Promise.allSettled([
      getSubjects({ params: { page: 1, page_size: 500 }, ...opts }),
      getPurposes({ params: { page: 1, page_size: 500 }, ...opts }),
      getObjectives({ params: { page: 1, page_size: 500 }, ...opts }),
      getStatuses({ params: { page: 1, page_size: 500 }, ...opts }),
    ]);
    setSubjectOptions(extract(subjects));
    setPurposeOptions(extract(purposes));
    setObjectiveOptions(extract(objectives));
    setStatusOptions(extract(statuses));
    setInitialDataLoaded(true);
  };

  // Counts of the status chips: the other filters apply, the status itself does not.
  const fetchStatusCount = async () => {
    if (countsControllerRef.current) {
      countsControllerRef.current.abort();
    }
    const controller = new AbortController();
    countsControllerRef.current = controller;
    const { status: _status, ...withoutStatus } = filterParams;
    try {
      const data = await getStatusCount({
        params: { page: 1, page_size: 100, ...withoutStatus },
        signal: controller.signal,
      });
      setStatusCounts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isCanceledError(err)) {
        console.error("Failed to fetch status count:", err);
      }
    }
  };

  const fetchTopics = async (signal) => {
    try {
      const topics = await getTopics({
        params: {
          page: 1,
          page_size: 100,
          subject: topicSubject,
          grade: grade !== 'all' ? grade : undefined,
        },
        signal,
      });
      setTopicOptions(topics?.results || []);
    } catch (err) {
      if (!isCanceledError(err)) {
        console.error("Failed to fetch topics:", err);
      }
    }
  };

  const fetchSubtopics = async (signal) => {
    try {
      const subtopics = await getSubtopics({
        params: {
          page: 1,
          page_size: 100,
          topic: topic !== 'all' ? topic : undefined,
        },
        signal,
      });
      setSubtopicOptions(subtopics?.results || []);
    } catch (err) {
      if (!isCanceledError(err)) {
        console.error("Failed to fetch subtopics:", err);
      }
    }
  };

  const fetchQuestions = async (pageNum = 1) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchedPageRef.current = pageNum;
    setLoading(true);

    try {
      const data = await getQuestions({
        params: {
          page: pageNum,
          page_size: PAGE_SIZE,
          ordering,
          ...filterParams,
        },
        signal: controller.signal,
      });

      const results = data?.results || [];
      setQuestions(results);
      setCount(data?.count || 0);
    } catch (err) {
      // A page that no longer exists (fewer results than before): back to the first one.
      if (err?.response?.status === 404 && pageNum > 1) {
        setPage(1);
        return;
      }
      if (!isCanceledError(err)) {
        console.error("Failed to fetch questions:", err);
        setQuestions([]);
      }
    } finally {
      // An aborted request must not hide the spinner of the request that replaced it.
      if (abortControllerRef.current === controller) setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (ordering === field) {
      setOrdering(`-${field}`);
    } else if (ordering === `-${field}`) {
      setOrdering('created');
    } else {
      setOrdering(field);
    }
  };

  // The links are built in the memoized columns: they save the latest filters from a ref, not those of its render.
  const filtersRef = useRef(null);
  filtersRef.current = {
    search,
    grade,
    subject,
    purpose,
    objective,
    topic,
    subtopic,
    status,
    folder,
    difficulty,
    ordering,
  };
  const handleStoreFilters = React.useCallback(() => {
    setFilterState(filtersRef.current);
  }, []);
  // A passage chip opens its passage on the Texts tab of My Questions.
  const openPassage = React.useCallback(
    (passage) => {
      handleStoreFilters();
      navigate(`/my-questions/texts?open=${passage.uuid}`);
    },
    [handleStoreFilters, navigate],
  );

  const openUploadModal = (targetFolder = null) => {
    setUploadFolder(targetFolder);
    setShowUploadModal(true);
  };

  // After an import: page 1 of the list, the status counts and the folder counts.
  const refreshAfterChange = () => {
    setPage(1);
    fetchQuestions(1);
    fetchStatusCount();
    loadFolders();
  };

  const handleDelete = (uuid) => {
    toast(
      ({ closeToast }) => (
        <div>
          <p className="mb-3 text-sm font-medium text-slate-900">{t('my_questions.delete.confirm')}</p>
          <div className="flex gap-2">
            <button
              className="inline-flex h-8 items-center justify-center rounded-lg bg-red-600 px-3 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              onClick={() => {
                deleteQuestion(uuid)
                  .then(() => {
                    toast.success(t('my_questions.delete.success'));
                    fetchQuestions(page);
                    fetchStatusCount();
                  })
                  .catch((err) => {
                    toast.error(getApiErrorMessage(t, err, 'my_questions.delete.failed'));
                  });
                closeToast();
              }}
            >
              {t('buttons.delete')}
            </button>
            <button
              className="inline-flex h-8 items-center justify-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              onClick={closeToast}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false }
    );
  };

  // Grade and subject narrow the topics, so they clear the chosen topic and subtopic.
  const changeGrade = (value) => {
    setGrade(value);
    setTopic('all');
    setSubtopic('all');
  };
  const changeSubject = (value) => {
    setSubject(value);
    setTopic('all');
    setSubtopic('all');
  };

  // Every filter back to "all"; the sort order stays.
  const handleReset = () => {
    setSearch('');
    setGrade('all');
    setSubject('all');
    setPurpose('all');
    setObjective('all');
    setTopic('all');
    setSubtopic('all');
    setStatus('all');
    setDifficulty('all');
  };

  const gradeOptions = [
    { value: 'all', label: t('my_questions.filters.all_grades') },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: t('questions_page.filters.grade_n', { grade: i + 1 }),
    })),
  ];

  // "null" = No subject (questions imported without one).
  const subjectSelectOptions = [
    { value: 'all', label: t('my_questions.filters.all_subjects') },
    ...subjectOptions.map((s) => ({ value: s.uuid, label: s.name })),
    ...(foldersAvailable ? [{ value: 'null', label: t('folders.no_subject') }] : []),
  ];

  const purposeSelectOptions = [
    { value: 'all', label: t('my_questions.filters.all_terms') },
    ...purposeOptions.map((p) => ({ value: p.uuid, label: p.name })),
  ];

  const objectiveSelectOptions = [
    { value: 'all', label: t('my_questions.filters.all_objectives') },
    ...objectiveOptions.map((o) => ({ value: o.uuid, label: o.name })),
  ];

  const topicSelectOptions = [
    { value: 'all', label: t('my_questions.filters.all_topics') },
    ...topicOptions.map((t) => ({ value: t.uuid, label: t.name })),
  ];

  const subtopicSelectOptions = [
    { value: 'all', label: t('my_questions.filters.all_subtopics') },
    ...subtopicOptions.map((s) => ({ value: s.uuid, label: s.name })),
  ];

  const difficultySelectOptions = [
    { value: 'all', label: t('difficulty.filter_all') },
    ...DIFFICULTY_LEVELS.map((level) => ({ value: level, label: difficultyLabel(t, level) })),
    { value: 'null', label: t('difficulty.filter_none') },
  ];

  const toggleSelect = useCallback((question) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(question.uuid)) next.delete(question.uuid);
      else next.add(question.uuid);
      return next;
    });
  }, []);
  const togglePage = useCallback((pageQuestions, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageQuestions.forEach((q) => (checked ? next.add(q.uuid) : next.delete(q.uuid)));
      return next;
    });
  }, []);
  // A draft is not a test's question yet: it can't be ticked (the preview takes it once it is sent for review).
  const selectBox = useCallback(
    (question) =>
      isDraftQuestion(question)
        ? { label: t('my_questions.preview.draft_disabled'), disabled: true }
        : { label: t('my_questions.preview.select', { code: question.code || question.name || '' }) },
    [t],
  );

  const columns = React.useMemo(
    () => [
      {
        Header: () => <PageCheckbox label={t('my_questions.preview.select_page')} />,
        id: 'select',
        Cell: (props) => <RowCheckbox question={props.row.original} />,
      },
      {
        Header: t('question_list.columns.question'),
        id: 'question',
        accessor_for_ordering: 'name',
        Cell: (props) => (
          <QuestionSummary
            question={props.row.original}
            href={questionHref(props.row.original)}
            onOpen={handleStoreFilters}
            onPassage={openPassage}
          />
        ),
      },
      {
        Header: t('question_list.columns.status'),
        id: 'status',
        accessor_for_ordering: 'status__name',
        Cell: (props) => <QuestionStatusBadge status={props.row.original.status} />,
      },
      {
        Header: t('difficulty.label'),
        accessor: 'difficulty',
        Cell: (props) => (props.value ? <DifficultyBadge level={props.value} /> : <Dash />),
      },
      ...(inSubject
        ? []
        : [
            {
              Header: t('question_list.columns.subject'),
              id: 'subject',
              Cell: (props) => <SubjectCell question={props.row.original} showFolder={showFolder} />,
            },
          ]),
      {
        Header: t('my_questions.columns.subtopic'),
        accessor: 'subtopic.name',
        Cell: (props) => (props.value ? <span className="text-slate-700">{props.value}</span> : <Dash />),
      },
      {
        Header: t('my_questions.columns.reviews'),
        id: 'reviews',
        Cell: (props) => <ReviewCounts question={props.row.original} />,
      },
      {
        Header: t('my_questions.columns.created'),
        accessor: 'created',
        accessor_for_ordering: 'created',
        Cell: (props) => <span className="tabular-nums text-slate-500">{formatDate(props.value, language)}</span>,
      },
      {
        Header: () => <span className="sr-only">{t('my_questions.columns.actions')}</span>,
        id: 'actions',
        Cell: (props) => {
          const href = editHref(props.row.original);
          if (!href) return null;
          const label = t('question_list.edit_question', { name: props.row.original.name || props.row.original.code || '' });
          return (
            <Link
              to={href}
              onClick={handleStoreFilters}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              title={label}
              aria-label={label}
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          );
        },
      },
    ],
    [t, language, inSubject, showFolder, handleStoreFilters, openPassage],
  );
  const selection = useMemo(
    () => ({ questions, selectedIds, toggleSelect, togglePage, selectBox }),
    [questions, selectedIds, toggleSelect, togglePage, selectBox],
  );

  // A subject shows its questions or its matrix, as in the question bank; another folder opens on questions.
  const [view, setView] = useState('questions');
  useEffect(() => setView('questions'), [folder]);
  useEffect(() => setSelectedIds(new Set()), [folder]);
  const matrixShown = inSubject && view === 'matrix';
  const tasksShown = inSubject && view === 'tasks' && canSeeTasksIn(currentSubject?.role);
  // The matrix's unsaved changes (FolderMatrix says when there are some) go with it: another folder or view asks
  // first, as the matrix's own Cancel does. Links and the sidebar ask through the matrix itself.
  const matrixDirtyRef = useRef(false);
  const handleMatrixDirty = React.useCallback((dirty) => {
    matrixDirtyRef.current = dirty;
  }, []);
  const leaveMatrix = () => !matrixDirtyRef.current || window.confirm(t('folders.matrix.discard_confirm'));
  const chooseFolder = (value) => {
    if (value === folder || leaveMatrix()) setFolder(value);
  };
  const chooseView = (value) => {
    if (value === view || leaveMatrix()) setView(value);
  };
  // "Share" of a subject (its moderator and admins); the folder stays set while the dialog closes.
  const [shareDialog, setShareDialog] = useState({ open: false, folder: null });
  const examOf = (f) => (f?.parent ? byUuid.get(f.parent) : f) || null;

  // Admin: an exam created (and opened) or its settings saved, a subject added, an exam or a subject deleted.
  const handleFolderSaved = (saved) => {
    const before = folderDialog.folder;
    setFolderDialog((d) => ({ ...d, open: false }));
    toast.success(examSavedMessage(t, before, saved));
    if (!before && saved?.uuid && leaveMatrix()) setFolder(saved.uuid);
    loadFolders();
  };
  const handleSubjectAdded = (saved) => {
    setSubjectDialog((d) => ({ ...d, open: false }));
    toast.success(t('folders.toast.subject_added'));
    if (saved?.uuid && leaveMatrix()) setFolder(saved.uuid);
    loadFolders();
  };
  const handleSubjectSaved = () => {
    setSubjectSettings((d) => ({ ...d, open: false }));
    toast.success(t('folders.toast.subject_saved'));
    loadFolders();
  };
  const handleFolderDeleted = (deleted) => {
    setDeleteDialog((d) => ({ ...d, open: false }));
    toast.success(deleted.parent ? t('folders.toast.subject_deleted') : t('folders.toast.exam_deleted'));
    if (folder === deleted.uuid) setFolder(deleted.parent || 'all');
    else if (!deleted.parent && byUuid.get(folder)?.parent === deleted.uuid) setFolder('all');
    loadFolders();
  };

  // Main filters on screen; the rarer ones behind "More filters". Inside a subject the subject filter is
  // the folder itself, so it is not offered there.
  const mainFilters = [
    !inSubject && {
      key: 'subject',
      active: subject !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.subject')}
          options={subjectSelectOptions}
          value={subject}
          onChange={(option) => changeSubject(option?.value || 'all')}
          placeholder={t('my_questions.filters.all_subjects')}
          isSearchable
        />
      ),
    },
    {
      key: 'topic',
      active: topic !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.topic')}
          options={topicSelectOptions}
          value={topic}
          onChange={(option) => {
            setTopic(option?.value || 'all');
            setSubtopic('all');
          }}
          placeholder={t('my_questions.filters.all_topics')}
          isSearchable
        />
      ),
    },
    {
      key: 'subtopic',
      active: subtopic !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.subtopic')}
          options={subtopicSelectOptions}
          value={subtopic}
          onChange={(option) => setSubtopic(option?.value || 'all')}
          placeholder={t('my_questions.filters.all_subtopics')}
          isSearchable
          isDisabled={topic === 'all'}
        />
      ),
    },
    {
      key: 'difficulty',
      active: difficulty !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('difficulty.label')}
          options={difficultySelectOptions}
          value={difficulty}
          onChange={(option) => setDifficulty(option?.value || 'all')}
          placeholder={t('difficulty.filter_all')}
          isSearchable={false}
        />
      ),
    },
  ].filter(Boolean);

  const moreFilters = [
    {
      key: 'grade',
      active: grade !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.grade')}
          options={gradeOptions}
          value={grade}
          onChange={(option) => changeGrade(option?.value || 'all')}
          placeholder={t('my_questions.filters.all_grades')}
          isSearchable
        />
      ),
    },
    {
      key: 'term',
      active: purpose !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.term')}
          options={purposeSelectOptions}
          value={purpose}
          onChange={(option) => setPurpose(option?.value || 'all')}
          placeholder={t('my_questions.filters.all_terms')}
          isSearchable
        />
      ),
    },
    {
      key: 'objective',
      active: objective !== 'all',
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t('question_list.filter.objective')}
          options={objectiveSelectOptions}
          value={objective}
          onChange={(option) => setObjective(option?.value || 'all')}
          placeholder={t('my_questions.filters.all_objectives')}
          isSearchable
        />
      ),
    },
  ];

  // The active filters that are not on screen: those behind "More filters", and the subject inside a subject.
  const filterChips = [
    inSubject && subject !== 'all' && {
      key: 'subject',
      label: t('question_list.filter.subject'),
      value: optionLabel(subjectSelectOptions, subject),
      onRemove: () => changeSubject('all'),
    },
    grade !== 'all' && {
      key: 'grade',
      label: t('question_list.filter.grade'),
      value: optionLabel(gradeOptions, grade),
      onRemove: () => changeGrade('all'),
    },
    purpose !== 'all' && {
      key: 'term',
      label: t('question_list.filter.term'),
      value: optionLabel(purposeSelectOptions, purpose),
      onRemove: () => setPurpose('all'),
    },
    objective !== 'all' && {
      key: 'objective',
      label: t('question_list.filter.objective'),
      value: optionLabel(objectiveSelectOptions, objective),
      onRemove: () => setObjective('all'),
    },
  ].filter(Boolean);

  const filtersActive =
    !!search || [grade, subject, purpose, objective, topic, subtopic, status, difficulty].some((value) => value !== 'all');
  const pageCount = Math.ceil(count / PAGE_SIZE);

  const cardFooter = (question) =>
    [question.folder ? folderPath(question.folder) : question.subject?.name, formatDate(question.created, language)]
      .filter(Boolean)
      .join(' · ');
  const cardAction = (question) => {
    const href = editHref(question);
    if (!href) return null;
    const label = t('question_list.edit_question', { name: question.name || question.code || '' });
    return (
      <Link
        to={href}
        onClick={handleStoreFilters}
        title={label}
        aria-label={label}
        className="-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        <PencilIcon className="h-4 w-4" aria-hidden="true" />
      </Link>
    );
  };

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* The guide's "?" right after the title. */}
            <HelpTitle
              helpId="my_questions"
              helpTitle={t('help.my_questions.title')}
              guide={<MyQuestionsGuide places={createPlaces} texts={passagesAvailable} />}
            >
              {t('my_questions.title')}
            </HelpTitle>
            <p className="mt-1 text-sm text-slate-500">{t('my_questions.subtitle')}</p>
          </div>
          {!inSubject && (
            // One main action: "Create question". Uploading a file is a quiet button beside it, and on phones it waits
            // in "⋯" (one row: the main button and "⋯").
            <div className="relative flex items-center gap-2 max-md:w-full">
              {wide && (
                <button type="button" onClick={() => openUploadModal()} className={quietButton}>
                  <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                  {t('buttons.upload_file')}
                </button>
              )}
              <CreateQuestionMenu
                folders={foldersAvailable ? folders : []}
                allowNoFolder={canCreateWithoutFolder}
                className="max-md:flex-1"
              />
              {!wide && (
                <OverflowMenu
                  label={t('my_questions.more_actions')}
                  items={[{ key: 'upload', label: t('buttons.upload_file'), Icon: ArrowUpTrayIcon, onClick: () => openUploadModal() }]}
                  testId="my-questions-actions"
                />
              )}
            </div>
          )}
        </div>
        <BankTabs active="questions" scope="mine" showTexts={passagesAvailable} onLeave={handleStoreFilters} />
      </div>

      {foldersAvailable && (
        <FolderBar
          folders={folders}
          noFolderCount={noFolderCount}
          selected={folder}
          onSelect={chooseFolder}
          onUpload={openUploadModal}
          isAdmin={isAdmin}
          onCreateExam={() => setFolderDialog({ open: true, folder: null })}
          onEditExam={(f) => setFolderDialog({ open: true, folder: f })}
          onAddSubject={(exam) => setSubjectDialog({ open: true, exam })}
          onEditSubject={(subject) => setSubjectSettings({ open: true, folder: subject })}
          onDeleteFolder={(f) => setDeleteDialog({ open: true, folder: f })}
          onShare={(subject) => setShareDialog({ open: true, folder: subject })}
          view={view}
          onViewChange={chooseView}
        />
      )}

      {preview}

      <UploadQuestionsDialog
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        folder={uploadFolder}
        folderLabel={folderPath(uploadFolder, byUuid)}
        allowNoFolder={canCreateWithoutFolder}
        defaultSubject={!uploadFolder && subject !== 'all' && subject !== 'null' ? subject : ''}
        subjects={subjectOptions}
        onUploaded={refreshAfterChange}
        onFolderDeleted={() => {
          setFolder('all');
          loadFolders();
        }}
      />

      <ShareFolderDialog
        isOpen={shareDialog.open}
        folder={shareDialog.folder}
        examName={examOf(shareDialog.folder)?.name || ''}
        onClose={() => setShareDialog((d) => ({ ...d, open: false }))}
      />

      {isAdmin && foldersAvailable && (
        <>
          <FolderNameDialog
            isOpen={folderDialog.open}
            folder={folderDialog.folder}
            onClose={() => setFolderDialog((d) => ({ ...d, open: false }))}
            onSaved={handleFolderSaved}
          />
          <AddSubjectDialog
            isOpen={subjectDialog.open}
            exam={exams.find((e) => e.uuid === subjectDialog.exam?.uuid) || subjectDialog.exam}
            subjects={subjectOptions}
            onClose={() => setSubjectDialog((d) => ({ ...d, open: false }))}
            onAdded={handleSubjectAdded}
          />
          <SubjectSettingsDialog
            isOpen={subjectSettings.open}
            folder={subjectSettings.folder}
            examName={examOf(subjectSettings.folder)?.name || ''}
            onClose={() => setSubjectSettings((d) => ({ ...d, open: false }))}
            onSaved={handleSubjectSaved}
          />
          <DeleteFolderDialog
            isOpen={deleteDialog.open}
            folder={deleteDialog.folder}
            examName={examOf(deleteDialog.folder)?.name || ''}
            onClose={() => setDeleteDialog((d) => ({ ...d, open: false }))}
            onDeleted={handleFolderDeleted}
          />
        </>
      )}

      {matrixShown ? (
        <FolderMatrix folder={currentSubject} folders={folders} onDirtyChange={handleMatrixDirty} />
      ) : tasksShown ? (
        <FolderTasks key={currentSubject.uuid} folder={currentSubject} />
      ) : (
      <>
      <StatusFilterBar counts={statusCounts} statusOptions={statusOptions} selected={status} onSelect={setStatus} showDraft />

      <FilterPanel
        search={search}
        onSearchChange={setSearch}
        filters={mainFilters}
        more={moreFilters}
        chips={filterChips}
        canClear={filtersActive}
        onClearAll={handleReset}
      />

      {/* Ticked questions turn the count line into their bar, whose main button shows them as a test. */}
      {selectedIds.size > 0 ? (
        <SelectionBar
          count={selectedIds.size}
          onClear={() => setSelectedIds(new Set())}
          page={page}
          pageCount={pageCount}
          action={{
            label: t('my_questions.preview.button', { count: selectedIds.size }),
            Icon: EyeIcon,
            onClick: () => openPreview([...selectedIds]),
            loading: previewLoading,
            disabled: selectedIds.size > MAX_PREVIEW,
            title: selectedIds.size > MAX_PREVIEW ? t('errors.too_many_questions') : undefined,
          }}
        />
      ) : (
        <ResultsBar
          count={count}
          page={page}
          pageCount={pageCount}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onPage={setPage}
          loading={loading}
          action={
            !loading && questions.length > 0 ? (
              <button
                type="button"
                onClick={() => openPreview(questions.map((q) => q.uuid))}
                disabled={previewLoading}
                title={t('my_questions.preview.page_button')}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-60 max-sm:w-11 max-sm:shrink-0 max-sm:px-0 sm:h-9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                {previewLoading ? <Spinner className="h-4 w-4 shrink-0" /> : <EyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                <span className="max-sm:sr-only">{t('my_questions.preview.page_button')}</span>
              </button>
            ) : null
          }
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <p className="text-sm text-slate-500">{t('my_questions.list.loading')}</p>
          </div>
        </div>
      ) : questions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 shadow-sm">
          <div className="mb-4 rounded-full bg-slate-100 p-3 text-slate-400">
            <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-slate-900">{t('my_questions.list.empty_title')}</h3>
          <p className="mt-1 text-center text-sm text-slate-500">
            {t('my_questions.list.empty_text')}
          </p>
          {filtersActive && (
            <button
              type="button"
              onClick={handleReset}
              className="mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              {t('question_list.clear_all')}
            </button>
          )}
        </div>
      ) : wide ? (
        <div className="min-w-0 overflow-hidden">
          <PreviewSelection.Provider value={selection}>
            <TableWithServerOrdering
              columns={columns}
              data={questions}
              ordering={ordering}
              handleOrdering={handleSort}
              loading={loading}
              hideCount
              dense
              getRowHref={questionHref}
              onRowOpen={handleStoreFilters}
            />
          </PreviewSelection.Provider>
        </div>
      ) : (
        <QuestionCards
          questions={questions}
          getHref={questionHref}
          onOpen={handleStoreFilters}
          onPassage={openPassage}
          footer={cardFooter}
          action={cardAction}
          selectable
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          checkbox={selectBox}
        />
      )}

      {questions.length > 0 && (
        <div className="flex justify-center">
          <Pager page={page} pageCount={pageCount} onPage={setPage} disabled={loading} label={t('question_list.pagination_bottom')} />
        </div>
      )}
      </>
      )}
    </div>
  );
}
